import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/utils/supabase';
import { useI18n } from '@/i18n';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { FaCircleArrowLeft, FaCircleArrowRight } from "react-icons/fa6";
import { 
  MapPin, CreditCard, Package, Tag,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { getLocationName } from '@/data/bangladesh-locations';
import { useOrderStore } from '@/store/orderStore';
import { OrderDetailSkeleton } from '@/components/skeletons/OrderSkeletons';
import { motion, AnimatePresence } from 'framer-motion';
import { OrderChat } from '@/components/OrderChat';

export function OrderDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t, currentLang } = useI18n();
  const { orders } = useOrderStore();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [order, setOrder] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Unified SWR: Cache-first → Instant Render → Silent Background Revalidation
  useEffect(() => {
    if (!id) return;

    // Step 1: Instant cache hit from global orderStore
    const cachedOrder = orders.find(o => o.id === id);
    if (cachedOrder) {
      setOrder(cachedOrder);
      setIsLoading(false); // Render in 0ms — no spinner
    }

    // Step 2: Silent background revalidation (always fetch fresh data)
    const fetchOrder = async () => {
      try {
        const { data, error } = await supabase
          .from('orders')
          .select(`
            *,
            order_items (
              *,
              products (*)
            )
          `)
          .eq('id', id)
          .single();

        if (error) throw error;
        setOrder(data); // Silently overwrite with freshest network data
      } catch (err: unknown) {
        // Only log/toast if we have NO cached fallback at all
        if (!cachedOrder) {
          console.error('Error fetching order:', err);
          toast.error(t('error.fetch_order') || 'Could not fetch order details');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrder();
  }, [id, orders, t]);


  if (!order) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
        <h2 className="text-xl font-bold text-black mb-4">{t('error.order_not_found') || 'Order not found'}</h2>
        <Button onClick={() => navigate('/orders')} variant="default" className="rounded-full px-8">
          {t('nav.orders')}
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-white font-sans animate-fade-in pb-24 lg:pb-10">
      {/* Premium Header — Gold Standard */}
      <header className="sticky top-0 z-50 w-full bg-white/80 backdrop-blur-xl border-b border-gray-100/50 shadow-[0_4px_20px_rgba(0,0,0,0.03)] transition-all">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-xl hover:bg-gray-50 text-black transition-all active:scale-95"
            onClick={() => navigate('/orders')}
          >
            <FaCircleArrowLeft className="h-6 w-6" />
          </Button>
          <h1 className="text-lg font-black tracking-tight text-center flex-1">{t('order.details')}</h1>
          <div className="w-10" /> {/* Spacer */}
        </div>
      </header>

      <AnimatePresence mode="wait">
        {isLoading && !order ? (
          <motion.div
            key="loading"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <OrderDetailSkeleton />
          </motion.div>
        ) : !order ? (
          <motion.div
            key="not-found"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="min-h-[60vh] flex flex-col items-center justify-center p-4 text-center"
          >
            <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
            <h2 className="text-xl font-bold text-black mb-4">{t('error.order_not_found') || 'Order not found'}</h2>
            <Button onClick={() => navigate('/orders')} variant="default" className="rounded-full px-8 text-white font-bold">
              {t('nav.orders')}
            </Button>
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="px-6 space-y-8 max-w-2xl mx-auto mt-6"
          >
            {/* Order Meta Info */}
            <div className="text-center space-y-1 border-b pb-6 border-gray-100">
                <p
                    className="text-sm text-gray-600 font-bold cursor-pointer hover:text-primary transition-colors"
                    onClick={() => {
                      navigator.clipboard.writeText(order.id);
                      toast.success(t('success.copied'));
                    }}
                    title="Click to copy full ID"
                >
                    #{order.id} • {new Date(order.created_at).toLocaleString(currentLang === 'bn' ? 'bn-BD' : 'en-US')}
                </p>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 bg-gray-50 rounded-2xl space-y-3 shadow-sm border border-gray-100/50">
                <h3 className="text-sm font-black flex items-center gap-2 text-black border-b pb-2 border-gray-200">
                  <MapPin className="h-4 w-4 text-primary" /> {t('checkout.delivery_info')}
                </h3>
                <div className="text-xs text-gray-600 leading-relaxed font-bold">
                  <p>{order.delivery_address?.street}</p>
                  <p>{order.delivery_address?.area && `${getLocationName(order.delivery_address.area, currentLang)}, `}{order.delivery_address?.city && getLocationName(order.delivery_address.city, currentLang)}</p>

                </div>
              </div>

              <div className="p-4 bg-gray-50 rounded-2xl space-y-3 shadow-sm border border-gray-100/50">
                <h3 className="text-sm font-black flex items-center gap-2 text-black border-b pb-2 border-gray-200">
                  <CreditCard className="h-4 w-4 text-primary" /> {t('checkout.payment_method')}
                </h3>
                <div className="text-xs text-gray-600 leading-relaxed font-bold space-y-1">
                  <p className="flex justify-between uppercase">
                    <span className="font-black text-black">{order.payment_method?.replace('_', ' ')}</span>
                    <Badge variant="outline" className={`text-[10px] font-black scale-90 ${order.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'}`}>
                      {order.payment_status}
                    </Badge>
                  </p>
                  <p>{t('form.phone')}: {order.contact_phone}</p>
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="space-y-4">
              <h3 className="text-sm font-black flex items-center gap-2 text-black">
                <Package className="h-4 w-4 text-primary" /> {t('order.items')}
              </h3>
              <div className="space-y-3">
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {order.order_items?.map((item: any) => (
                  <div key={item.id} className="flex items-center gap-4 p-3.5 bg-white rounded-3xl shadow-[0px_6px_20px_rgba(0,0,0,0.03)] transition-all hover:shadow-[0px_10px_30px_rgba(0,0,0,0.06)] active:scale-[0.99]">
                    {item.products?.images?.[0] ? (
                      <img src={item.products.images[0]} alt={item.products.name_en} className="w-16 h-16 object-cover rounded-2xl shadow-[0px_4px_12px_rgba(0,0,0,0.08)]" />
                    ) : (
                      <div className="w-16 h-16 bg-gray-50 rounded-2xl flex items-center justify-center text-gray-400">
                        <Package size={24} />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-black text-black truncate">
                        {currentLang === 'bn' 
                          ? (item.product_name_bn || item.products?.name_bn || 'Product') 
                          : (item.product_name_en || item.products?.name_en || 'Product')
                        }
                      </p>
                      <p className="text-[11px] text-gray-400 font-black uppercase tracking-wider flex items-center gap-1.5 mt-0.5">
                        <span className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center text-[9px] text-gray-600 font-black">{item.quantity}</span>
                         x Tk {item.price_at_time}
                      </p>
                    </div>
                    <p className="text-[15px] font-black text-black pr-2">Tk {item.total}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Summary */}
            <div className="p-5 bg-gray-50 rounded-2xl text-black space-y-3 shadow-sm border border-gray-100">
              <div className="flex justify-between text-sm items-baseline">
                <span className="text-gray-600 font-bold">{t('cart.subtotal')}</span>
                <span className="font-black">Tk {order.subtotal}</span>
              </div>
              <div className="flex justify-between text-sm items-baseline">
                <span className="text-gray-600 font-bold">{t('cart.delivery_fee')}</span>
                <span className="font-black">Tk {order.delivery_fee}</span>
              </div>
              {order.discount > 0 && (
                <div className="flex justify-between text-sm items-baseline text-emerald-600">
                  <div className="flex items-center gap-1">
                    <Tag className="h-3 w-3" />
                    <span className="font-bold">{t('cart.discount')}</span>
                  </div>
                  <span className="font-black">-Tk {order.discount}</span>
                </div>
              )}
              <Separator className="bg-gray-200" />
              <div className="flex justify-between text-lg items-baseline">
                <span className="font-black">{t('cart.total')}</span>
                <span className="text-xl font-black text-primary">Tk {order.total}</span>
              </div>
            </div>

            <div className="pb-10">
                <Button 
                    variant="outline" 
                    className="w-full rounded-2xl border-gray-100 font-black h-12 flex items-center justify-center gap-2 group transition-all" 
                    onClick={() => navigate(`/orders/${id}/track`)}
                >
                    {t('nav.track_order')}
                    <FaCircleArrowRight className="h-4 w-4 text-primary transition-transform group-hover:translate-x-1" />
                </Button>
            </div>

            {/* Order Chat Widget */}
            {order && order.id && (
              <OrderChat orderId={order.id} />
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
